import {
  ReportsTransferredBadge,
  type ReportsTransferChainHop,
} from '@/components/candidate-job-slider-v1/components/common/TransferredBadge';
import { KImage, KText } from '@/ui-library/components';
import { Flex, Link } from '@chakra-ui/react';

interface ApplicantNameCellProps {
  atsApplication: {
    id?: number;
    contact?: { image_url?: string; first_name?: string; last_name?: string };
    is_transferred_in?: boolean;
    transferred_from?: {
      job_name?: string;
      transferred_by?: { name?: string };
      transferred_at?: string;
    };
    is_transferred_out?: boolean;
    transferred_to?: {
      job_id?: number;
      application_id?: number;
      job_name?: string;
      transferred_by?: { name?: string };
      transferred_at?: string;
    };
    // Current active leaf of the transfer chain (end of A->B->C) — the redirect target so the
    // link lands on the latest active application instead of the immediate next hop.
    active_application?: {
      job_id?: number;
      application_id?: number;
    };
    transfer_chain?: ReportsTransferChainHop[];
  };
  jobId: number | string;
}

export const ApplicantNameCell = ({
  atsApplication,
  jobId: _job_id,
}: ApplicantNameCellProps) => {
  const {
    contact = {},
    is_transferred_out,
    transferred_to,
    active_application,
    id,
    is_transferred_in,
    transferred_from,
    transfer_chain,
  } = atsApplication || {};
  const { job_id, application_id } = transferred_to || {};
  // Transferred-out rows link to the chain's current active leaf; fall back to the immediate
  // next hop if the leaf isn't resolved.
  const transferredOutHref = active_application?.application_id
    ? `/jobs/${active_application.job_id}?tab=candidates&appId=${active_application.application_id}`
    : `/jobs/${job_id}?tab=candidates&appId=${application_id}`;
  const { image_url, first_name, last_name } = contact || {};
  // Show the transfer history for anyone transferred in (destination or intermediary job);
  // the "transferred to" badge only for a pure origin job — so intermediary rows show a single
  // history badge instead of two icons.
  const showTransferredInBadge = is_transferred_in && transferred_from;
  const showTransferredOutBadge =
    is_transferred_out && transferred_to && !is_transferred_in;
  return (
    <>
      {first_name ? (
        <Flex alignItems="center" gap="6px" w="100%" minW="0">
          <Link
            href={
              is_transferred_out
                ? transferredOutHref
                : `/jobs/${_job_id}?tab=candidates&appId=${id}`
            }
            target="_blank"
            rel="noopener noreferrer"
            display="flex"
            alignItems="center"
            gap="6px"
            minW="0"
            overflow="hidden"
          >
            <KImage
              name={`${first_name} ${last_name}`}
              src={image_url}
              backgroundStyles={{ minWidth: '20px', minHeight: '20px' }}
            />
            <KText
              maxW="100%"
              enableTooltip={true}
              isTruncated
              size="sm-regular"
              _hover={{
                textDecoration: 'underline',
                color: 'primary-500',
                cursor: 'pointer',
              }}
              textTransform="capitalize"
            >
              {`${first_name || ''} ${last_name || ''}`}
            </KText>
          </Link>
          {showTransferredInBadge && (
            <ReportsTransferredBadge
              type="from"
              jobName={transferred_from.job_name}
              transferredBy={transferred_from.transferred_by}
              transferredAt={transferred_from.transferred_at}
              chain={transfer_chain}
            />
          )}
          {showTransferredOutBadge && (
            <ReportsTransferredBadge
              type="to"
              jobName={transferred_to.job_name}
              transferredBy={transferred_to.transferred_by}
              transferredAt={transferred_to.transferred_at}
            />
          )}
        </Flex>
      ) : (
        '-'
      )}
    </>
  );
};
